import React, { useState } from 'react';
import { FolderOpen, Upload, Grid, MoreVertical, ChevronDown, FileText, Image as ImageIcon, Film, File } from 'lucide-react';

const RealEstateAssetsNew = () => {
  const [selectedFile, setSelectedFile] = useState({
    name: 'Beachfront_Condo.pdf',
    size: '4.2 mb',
    preview: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400&h=300&fit=crop',
    type: 'pdf',
    comments: [
      {
        user: 'Sarah M.',
        initials: 'SM',
        time: '2:45 PM',
        text: 'Great location! Oceanfront views are stunning! 🌊'
      }
    ]
  });

  const folders = [
    { name: 'Legal Documents', files: 342, size: '1.2gb', users: ['AS', 'MD', 'JC', '+8'] },
    { name: 'Property Listings', files: 156, size: '892mb', users: ['RF', 'TL', 'LM', '+12'] },
    { name: 'Marketing Materials', files: 248, size: '2.4gb', users: ['NC', 'KR', '+6'] },
    { name: 'Financial Reports', files: 89, size: '245mb', users: ['DR', 'SN', 'BT', '+3'] }
  ];

  const files = [
    { 
      name: 'Purchase_Agreement.pdf', 
      size: '3.2 mb', 
      type: 'pdf',
      gradient: 'from-purple-500 to-pink-500',
      progress: null
    },
    { 
      name: 'Property_Photos.zip', 
      size: '127.8 mb', 
      type: 'image',
      preview: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=300&h=200&fit=crop',
      progress: null
    },
    { 
      name: 'Floor_Plans.dwg', 
      size: '8.4 mb', 
      type: 'cad',
      preview: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=300&h=200&fit=crop',
      progress: null
    },
    { 
      name: 'Virtual_Tour.mp4', 
      size: '95.6 mb', 
      type: 'video',
      preview: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=300&h=200&fit=crop',
      duration: '3:42'
    },
    { 
      name: 'Title_Report.pdf', 
      size: '2.8 mb', 
      type: 'pdf',
      gradient: 'from-orange-500 to-red-500',
      progress: null
    },
    { 
      name: 'Inspection_Report.pdf', 
      size: '5.6 mb', 
      type: 'pdf',
      gradient: 'from-blue-500 to-cyan-500',
      progress: null
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-1">Assets & Files</h1>
          <p className="text-gray-400 text-sm">All property documents and marketing materials</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-black border border-white/40 rounded-lg text-sm hover:border-white/60 transition-all">
            <span>This week</span>
            <ChevronDown size={16} />
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-black border border-white/40 rounded-lg text-sm hover:border-white/60 transition-all">
            <Grid size={16} />
            <span>Change view</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Main Content */}
        <div className="col-span-8">
          {/* Folders Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold">Folders</h2>
                <span className="text-gray-400 text-sm">4</span>
                <Grid size={16} className="text-gray-400" />
              </div>
              <button className="flex items-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-all">
                <Upload size={18} />
                <span>Upload</span>
              </button>
            </div>

            <div className="grid grid-cols-4 gap-4">
              {folders.map((folder, index) => (
                <div
                  key={index}
                  className="bg-black border border-white/40 rounded-xl p-4 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <FolderOpen size={20} className="text-blue-400" />
                    </div>
                    <button className="text-gray-400 hover:text-white transition-colors">
                      <MoreVertical size={18} />
                    </button>
                  </div>
                  <div className="text-sm font-semibold mb-1">{folder.name}</div>
                  <div className="text-xs text-gray-400 mb-3">{folder.files} files / {folder.size}</div>
                  <div className="flex items-center gap-1">
                    {folder.users.map((user, idx) => (
                      <div
                        key={idx}
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                          user.startsWith('+') 
                            ? 'bg-gray-700 text-gray-300' 
                            : 'bg-gradient-to-br from-purple-500 to-blue-500 text-white'
                        }`}
                      >
                        {user}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Files Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-xl font-bold">Files</h2>
              <span className="text-gray-400 text-sm">156</span>
              <Grid size={16} className="text-gray-400" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              {files.map((file, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedFile({
                    name: file.name,
                    size: file.size,
                    preview: file.preview || null,
                    type: file.type,
                    gradient: file.gradient,
                    duration: file.duration,
                    comments: selectedFile.comments
                  })}
                  className="bg-black border border-white/40 rounded-xl overflow-hidden hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300 cursor-pointer"
                >
                  {/* File Preview */}
                  <div className="aspect-[4/3] bg-gray-900 relative">
                    {file.preview ? (
                      <>
                        <img
                          src={file.preview}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                        {file.duration && (
                          <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 rounded text-xs font-semibold">
                            {file.duration}
                          </div>
                        )}
                        {file.progress && (
                          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span>Uploading...</span>
                              <span>{file.progress}%</span>
                            </div>
                            <div className="w-full h-1 bg-gray-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-500 transition-all duration-300"
                                style={{ width: `${file.progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </>
                    ) : file.gradient ? (
                      <div className={`w-full h-full bg-gradient-to-br ${file.gradient} flex items-center justify-center`}>
                        <FileText size={48} className="text-white opacity-50" />
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                        <File size={48} className="text-white opacity-50" />
                      </div>
                    )}
                  </div>

                  {/* File Info */}
                  <div className="p-3">
                    <div className="text-sm font-medium mb-1 truncate">{file.name}</div>
                    <div className="text-xs text-gray-400">{file.size}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* File Information Panel */}
        <div className="col-span-4">
          <div className="bg-black border border-white/40 rounded-xl p-6 sticky top-6">
            <h2 className="text-xl font-bold mb-4">File information</h2>

            {/* File Preview */}
            <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden mb-4">
              {selectedFile.preview ? (
                <img
                  src={selectedFile.preview}
                  alt={selectedFile.name}
                  className="w-full h-full object-cover"
                />
              ) : selectedFile.gradient ? (
                <div className={`w-full h-full bg-gradient-to-br ${selectedFile.gradient} flex items-center justify-center`}>
                  <FileText size={64} className="text-white opacity-50" />
                </div>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                  <File size={64} className="text-white opacity-50" />
                </div>
              )}
            </div>

            {/* File Details */}
            <div className="mb-6">
              <div className="text-lg font-semibold mb-1">{selectedFile.name}</div>
              <div className="text-sm text-gray-400">{selectedFile.size}</div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-4 mb-6 border-b border-white/10">
              <button className="pb-3 text-sm font-medium text-gray-400 hover:text-white transition-colors">
                Description
              </button>
              <button className="pb-3 text-sm font-medium border-b-2 border-blue-500 text-white transition-colors">
                Comments
              </button>
            </div>

            {/* Comments */}
            <div className="mb-8">
              {selectedFile.comments && selectedFile.comments.map((comment, index) => (
                <div key={index} className="flex gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {comment.initials}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{comment.user}</span>
                      <span className="text-xs text-gray-400">{comment.time}</span>
                    </div>
                    <div className="text-sm text-gray-300">{comment.text}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Storage Section */}
            <div className="pt-6 border-t border-white/10">
              <div className="text-sm font-semibold mb-4">Available storage</div>
              <div className="flex flex-col items-center">
                <div className="relative w-32 h-32 mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-gray-800"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 56}`}
                      strokeDashoffset={`${2 * Math.PI * 56 * (1 - 0.68)}`}
                      className="text-blue-500 transition-all duration-500"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold">68%</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold mb-1">1.4 / 2 gb</div>
                  <div className="text-xs text-gray-400">Storage used</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealEstateAssetsNew;
